import { Notice } from "@sk/ui";
import { Home, Lock, MessageCircle, Search as SearchIcon, Trophy, User } from "lucide-react";
import { type ComponentType, lazy as reactLazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Route, Routes, useLocation } from "react-router";
import { ErrorBoundary } from "./components/ErrorBoundary.tsx";
import { Login } from "./features/Login.tsx";
import { allTexts } from "./i18n/index.ts";
import { useInstallCard } from "./lib/install.ts";
import { useSession } from "./lib/session.ts";
import { useTheme } from "./lib/theme.ts";

// Un écran chargé à la demande attend aussi les textes complets (fr.json), qui arrivent à part.
// biome-ignore lint/suspicious/noExplicitAny: même signature que React.lazy
const lazy = <T extends ComponentType<any>>(load: () => Promise<{ default: T }>) =>
  reactLazy(() => allTexts.then(load));

// Écrans chargés à la demande : budget JS initial < 200 Ko compressés (3G, Android d'entrée de gamme).
const SyncBadge = lazy(() => import("./components/SyncBadge.tsx").then((m) => ({ default: m.SyncBadge })));
// Planche de style : en développement seulement, absente du build de production.
const Club = lazy(() => import("./features/Club.tsx").then((m) => ({ default: m.Club })));
const DeclaredMatches = lazy(() =>
  import("./features/DeclaredMatches.tsx").then((m) => ({ default: m.DeclaredMatches })),
);
const Conversation = lazy(() =>
  import("./features/Conversation.tsx").then((m) => ({ default: m.Conversation })),
);
const DesignGallery = import.meta.env.DEV
  ? lazy(() => import("./features/DesignGallery.tsx").then((m) => ({ default: m.DesignGallery })))
  : null;
const Feed = lazy(() => import("./features/Feed.tsx").then((m) => ({ default: m.Feed })));
const MatchSheet = lazy(() => import("./features/MatchSheet.tsx").then((m) => ({ default: m.MatchSheet })));
const Me = lazy(() => import("./features/Me.tsx").then((m) => ({ default: m.Me })));
const Messages = lazy(() => import("./features/Messages.tsx").then((m) => ({ default: m.Messages })));
const Onboarding = lazy(() => import("./features/Onboarding.tsx").then((m) => ({ default: m.Onboarding })));
const MyPosts = lazy(() => import("./features/MyPosts.tsx").then((m) => ({ default: m.MyPosts })));
const Team = lazy(() => import("./features/Team.tsx").then((m) => ({ default: m.Team })));
const Watchlist = lazy(() => import("./features/Watchlist.tsx").then((m) => ({ default: m.Watchlist })));
const MyInfo = lazy(() => import("./features/MyInfo.tsx").then((m) => ({ default: m.MyInfo })));
const Visibility = lazy(() => import("./features/Visibility.tsx").then((m) => ({ default: m.Visibility })));
const UnreadDot = lazy(() => import("./components/UnreadDot.tsx").then((m) => ({ default: m.UnreadDot })));
const NotificationBell = lazy(() =>
  import("./components/NotificationBell.tsx").then((m) => ({ default: m.NotificationBell })),
);
const InstallCard = lazy(() =>
  import("./components/InstallCard.tsx").then((m) => ({ default: m.InstallCard })),
);
const Notifications = lazy(() =>
  import("./features/Notifications.tsx").then((m) => ({ default: m.Notifications })),
);
const OpenMatch = lazy(() => import("./features/OpenMatch.tsx").then((m) => ({ default: m.OpenMatch })));
const OpenMatches = lazy(() =>
  import("./features/OpenMatches.tsx").then((m) => ({ default: m.OpenMatches })),
);
const ParentConsent = lazy(() =>
  import("./features/ParentConsent.tsx").then((m) => ({ default: m.ParentConsent })),
);
const Passport = lazy(() => import("./features/Passport.tsx").then((m) => ({ default: m.Passport })));
const Player = lazy(() => import("./features/Player.tsx").then((m) => ({ default: m.Player })));
const Recruiter = lazy(() => import("./features/Recruiter.tsx").then((m) => ({ default: m.Recruiter })));
const Score = lazy(() => import("./features/Score.tsx").then((m) => ({ default: m.Score })));
const Search = lazy(() => import("./features/Search.tsx").then((m) => ({ default: m.Search })));

const loadingScreen = <p className="sk-container">Chargement…</p>;

const NAV = [
  { to: "/", key: "feed", Icon: Home },
  { to: "/talents", key: "search", Icon: SearchIcon },
  { to: "/matchs", key: "matches", Icon: Trophy },
  { to: "/messages", key: "messages", Icon: MessageCircle },
  { to: "/moi", key: "me", Icon: User },
] as const;

export function App() {
  const { t } = useTranslation();
  const { session, profile, loading } = useSession();
  const theme = useTheme((s) => s.theme);
  const { pathname } = useLocation();
  // Dans une conversation, la zone de saisie prend la place de la barre du bas.
  const inThread = pathname.startsWith("/messages/");
  const install = useInstallCard();
  // Jamais pendant une saisie de match ni dans une conversation ; « Moi » a sa propre carte.
  const showInstall =
    install.visible && !inThread && !pathname.startsWith("/feuille/") && !pathname.startsWith("/moi");

  if (loading) return null;
  if (!session) return <Login />;
  // Compte suspendu ou banni : la base refuse déjà toute action ; on le dit clairement au lieu de laisser échouer.
  if (profile && profile.account_status !== "active")
    return (
      <main className="sk-container sk-form" style={{ display: "grid", gap: 16, alignContent: "start" }}>
        <h1 className="sk-h1">{t("blocked.title")}</h1>
        <Notice tone="warning" role="alert" icon={<Lock size={18} />}>
          {t("blocked.text")}
        </Notice>
        <button
          type="button"
          onClick={() => void import("./lib/forget.ts").then((m) => m.signOutAndForget())}
          style={{
            justifySelf: "start",
            minHeight: 44,
            border: 0,
            padding: 0,
            background: "none",
            font: "inherit",
            color: "var(--sk-link)",
            textDecoration: "underline",
          }}
        >
          {t("onboarding.signOut")}
        </button>
      </main>
    );
  if (profile && !profile.onboarding_completed_at)
    return (
      <Suspense fallback={loadingScreen}>
        <Onboarding />
      </Suspense>
    );

  return (
    <>
      <header
        className="sk-shell"
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <img
          src={theme === "dark" ? "/brand/logo-negative.svg" : "/brand/logo-positive.svg"}
          alt="SportKonnect"
          height={28}
          style={{ height: 28, width: "auto" }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Suspense fallback={null}>
            <SyncBadge />
            <NotificationBell uid={session.user.id} />
          </Suspense>
        </div>
      </header>
      {/* M3 : un moins de 15 ans sait en permanence que personne d'autre ne voit son profil. */}
      {profile?.minor_status === "private_until_consent" && (
        <div className="sk-shell" style={{ paddingTop: 0 }}>
          <Notice icon={<Lock size={18} />}>
            <strong>{t("privacy.privateTitle")}.</strong> {t("privacy.under15")}{" "}
            <NavLink to="/moi/accord" style={{ fontWeight: 600 }}>
              {t("consent.open")}
            </NavLink>
          </Notice>
        </div>
      )}
      {showInstall && (
        <div className="sk-shell" style={{ paddingTop: 0 }}>
          <Suspense fallback={null}>
            <InstallCard />
          </Suspense>
        </div>
      )}
      <ErrorBoundary resetKey={pathname}>
        <Suspense fallback={loadingScreen}>
          <Routes>
            <Route path="/" element={<Feed />} />
            <Route path="/talents" element={<Search />} />
            <Route path="/joueur/:id" element={<Player />} />
            <Route path="/matchs" element={<OpenMatches />} />
            <Route path="/matchs/:id" element={<OpenMatch />} />
            <Route path="/feuille/:matchId" element={<MatchSheet />} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/messages/:id" element={<Conversation />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/moi" element={<Me />} />
            <Route path="/moi/passeport" element={<Passport />} />
            <Route path="/moi/publications" element={<MyPosts />} />
            <Route path="/moi/club" element={<Club />} />
            <Route path="/moi/recruteur" element={<Recruiter />} />
            <Route path="/moi/visibilite" element={<Visibility />} />
            <Route path="/moi/matchs" element={<DeclaredMatches />} />
            <Route path="/moi/suivi" element={<Watchlist />} />
            <Route path="/moi/infos" element={<MyInfo />} />
            <Route path="/moi/score" element={<Score />} />
            <Route path="/moi/accord" element={<ParentConsent />} />
            <Route path="/moi/club/equipe/:teamId" element={<Team />} />
            {DesignGallery && <Route path="/design" element={<DesignGallery />} />}
          </Routes>
        </Suspense>
      </ErrorBoundary>
      {/* Barre du bas flottante, en pilule : l'onglet actif porte son nom, les autres leur icône (360 px). */}
      {!inThread && (
        <nav className="sk-nav" aria-label={t("nav.label")}>
          {NAV.map(({ to, key, Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              aria-label={t(`nav.${key}`)}
              style={{ position: "relative" }}
            >
              {({ isActive }) => (
                <>
                  <Icon size={22} aria-hidden />
                  {isActive && <span className="sk-nav-label">{t(`nav.${key}`)}</span>}
                  {key === "messages" && !isActive && (
                    <Suspense fallback={null}>
                      <UnreadDot uid={session.user.id} />
                    </Suspense>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>
      )}
    </>
  );
}
